import React, { useEffect, useState } from 'react';
import ChatbotImg from "../../images/chat-bot.svg";
import SearchIcon from "../../images/search.svg";
import CircleXIcon from "../../images/circle-x.svg";
import { findBestMatchSearchResult, getSearchResults } from '../../data/dataApi';
import { useNavigate } from 'react-router-dom';
import { sanitizeSearchResult } from '../helpers';
import { IconHelp, IconThumbUp, IconHistory, IconUsers } from '@tabler/icons-react';

const DashboardTopBar = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const initialBestMatch = {
        id: null,
        type: null,
    };
    const [bestMatch, setBestMatch] = useState(initialBestMatch);
    const [filters, setFilters] = useState([
        {
            type: 'History',
            active: true,
            show: true,
            icon: IconHistory
        },
        {
            type: 'Feedback',
            active: true,
            show: true,
            icon: IconThumbUp
        },
        {
            type: 'Help Requests',
            active: true,
            show: true,
            icon: IconHelp
        },
        {
            type: 'User Accounts',
            active: true,
            show: true,
            icon: IconUsers
        }
    ]);
    const [searchResults, setSearchResults] = useState([]);
    const [filteredSearchResults, setFilteredSearchResults] = useState([]);
    const [searchError, setSearchError] = useState(false);

    const navigate = useNavigate();

    const handleSearch = val => {
        setSearchQuery(val);

        if (val) {
            (async () => {
                setSearchError(false);

                const response = await getSearchResults(val, 5);

                if (response.success) {
                    let { chatHistory, feedback, helpRequests, userAccounts } = response.data;

                    const results = [
                        ...chatHistory.rows.map(item => ({ ...item, type: 'History', link: `/dashboard/history/${item.session_id}` })),
                        ...feedback.rows.map(item => ({ ...item, type: 'Feedback', link: `/dashboard/feedback/${item.id}` })),
                        ...helpRequests.rows.map(item => ({ ...item, type: 'Help Requests', link: `/dashboard/help-requests/${item.id}` })),
                        ...userAccounts.rows.map(item => ({ ...item, type: 'User Accounts', link: `/dashboard/user-accounts/${item.id}` }))
                    ];

                    setFilters(filters.map(filter => {
                        if (results.some(result => result.type === filter.type)) {
                            return {
                                ...filter,
                                show: true
                            };
                        } else {
                            return {
                                ...filter,
                                show: false
                            };
                        }
                    }));

                    const bestMatchResult = results.length ? await findBestMatchSearchResult(val, results.map(result => result.api_response || result.message || result.name)) : null;

                    setBestMatch(results.length && bestMatchResult ? results[bestMatchResult.data.bestMatch.index] : initialBestMatch);
                    setSearchResults(results);
                } else if (response.error) {
                    setSearchError(response.error.message);
                }
            })();
        } else {
            setSearchResults([]);
            setFilteredSearchResults([]);
            setBestMatch(initialBestMatch);
        }
    };

    const highlightMatchingCharacters = (result, query) => {
        const highlightText = (text, query) => {
            const regex = new RegExp(`(${query})`, 'gi');
            return text.replace(regex, '<span class="tif-color-white">$1</span>');
        };

        const truncateText = (text, query) => {
            if (text.length >= 300) {
                const matchIndex = text.toLowerCase().indexOf(query.toLowerCase());
                if (matchIndex === -1) {
                    return text.substring(0, 300);
                }
                const start = Math.max(0, matchIndex - 150);
                const end = Math.min(text.length, matchIndex + 150);
                return text.substring(start, end);
            } else {
                return text;
            }
        };

        const truncatedText = truncateText(result, query);
        const highlightedText = highlightText(truncatedText, query);

        return highlightedText;
    };

    const handleSearchResultClick = path => {
        navigate(path);
        setSearchResults([]);
        setSearchQuery("");
        setBestMatch(initialBestMatch);
    };

    const handleFilterChange = async type => {
        setFilters(filters.map(filter => {
            if (filter.type === type) {
                return {
                    ...filter,
                    active: !filter.active
                };
            } else {
                return filter;
            }
        }));
    };

    const ResultType = ({ type }) => {
        if (type === 'History') {
            return (
                <>
                    <IconHistory stroke={2} size="12px" />History
                </>
            );
        } else if (type === 'Feedback') {
            return (
                <>
                    <IconThumbUp stroke={2} size="12px" />Feedback
                </>
            );
        } else if (type === 'Help Requests') {
            return (
                <>
                    <IconHelp stroke={2} size="12px" />Help Requests
                </>
            );
        } else if (type === 'User Accounts') {
            return (
                <>
                    <IconUsers stroke={2} size="12px" />User Accounts
                </>
            );
        }
    };

    let results = searchResults;

    if (filteredSearchResults.length || filters.some(filter => !filter.active)) {
        results = filteredSearchResults;
    }

    const filterActive = filter => filters.some(f => f.type === filter && f.active) ;

    const excludeBestMatch = results => results.filter(result => bestMatch.id !== result.id || bestMatch.type !== result.type);

    useEffect(() => {
        (async () => {
            if (searchResults.length && filters.some(f => !f.active)) {
                setFilteredSearchResults(searchResults.filter(result => filterActive(result.type)));
            } else if(filters.some(f => f.active)) {
                setFilteredSearchResults([]);
            }
        })();
    }, [filters]);

    useEffect(() => {
        if (results.length && searchQuery) {
            (async () => {
                const bestMatchResult = results.length ? await findBestMatchSearchResult(searchQuery, results.map(result => result.api_response || result.message || result.name)) : null;

                setBestMatch(bestMatchResult ? results[bestMatchResult.data.bestMatch.index] : initialBestMatch);
            })();
        } else {
            setBestMatch(initialBestMatch);
        }
    }, [searchResults, filteredSearchResults]);

    return (
        <div id="top-bar" className="tif-bg-color-secondary tif-bg-light-color-white tif-px-25 tif-px-md-50 tif-py-25">
            <div className="title-holder tif-mr-md-55">
                <img className="chat-bot-img tif-mr-30" src={ChatbotImg} alt="Gelest AI chatbot logo" />
                <h1 className="title tif-mb-0 tif-h2 tif-color-white tif-light-color-november">Gelest Navigator AI Assistant</h1>
            </div>
            <div className="search-holder posituion-relative">
                <div className="input-holder position-relative">
                    <img src={SearchIcon} className="search-icon" alt="Search icon" />
                    <input className={`tif-bg-color-bravo tif-color-white tif-bg-light-color-papa tif-light-color-november tif-font-sm tif-py-10 tif-pl-55 tif-pr-20 ${searchQuery ? 'focused' : ''}`} type="text" placeholder="Search" value={searchQuery} onChange={e => handleSearch(e.target.value)} />
                    {
                        searchQuery &&
                        <button id="clear-search-btn" className="bg-transparent tif-p-0 border-0" onClick={() => handleSearch("")}>
                            <img src={CircleXIcon} className="circle-x-icon" alt="X icon" />
                        </button>
                    }
                </div>

                {
                    (searchQuery || searchResults.length || bestMatch.id || searchError) ?
                        <div className="search-results position-absolute overflow-y-auto">
                            <h2>Results</h2>
                            <ul className="list-unstyled search-results-pills">
                                {
                                    filters.filter(filter => filter.show).map((filter, index) =>
                                        <li key={index} className={filter.active ? 'active' : ''} onClick={() => handleFilterChange(filter.type)}><filter.icon stroke={2} size="12px" />{filter.type}</li>
                                    )
                                }
                            </ul>

                            {
                                bestMatch.id ?
                                    <>
                                        <p className="search-results-list-label text-uppercase">Best Match</p>
                                        <ul className="search-results-list list-unstyled tif-mb-20">
                                            <li className="result tif-pt-5 tif-pr-15 tif-pb-10 tif-pl-10" onClick={() => handleSearchResultClick(bestMatch.link)}>
                                                <p className="result-heading tif-font-xs tif-mb-5">
                                                    <ResultType type={bestMatch.type} />
                                                </p>
                                                <p className="tif-font-xs tif-m-0" dangerouslySetInnerHTML={{ __html: bestMatch.user_message }} />
                                                <p className="tif-font-xs tif-m-0" dangerouslySetInnerHTML={{ __html: highlightMatchingCharacters(sanitizeSearchResult(bestMatch.api_response || bestMatch.message || bestMatch.name), searchQuery) }} />
                                            </li>
                                        </ul>
                                    </>
                                    :
                                    <></>
                            }

                            {
                                excludeBestMatch(results).length ?
                                    <>
                                        <p className="search-results-list-label text-uppercase">Other Results ({excludeBestMatch(results).length})</p>
                                        <ul className="search-results-list list-unstyled tif-mb-0">
                                            {
                                                excludeBestMatch(results).map((result, index) =>
                                                    <li className="result tif-pt-5 tif-pr-15 tif-pb-10 tif-pl-10" key={index} onClick={() => handleSearchResultClick(result.link)}>
                                                        <p className="result-heading tif-font-xs tif-mb-5">
                                                            <ResultType type={result.type} />
                                                        </p>
                                                        <p className="tif-font-xs tif-m-0" dangerouslySetInnerHTML={{ __html: result.user_message }} />
                                                        <p className="tif-font-xs tif-m-0" dangerouslySetInnerHTML={{ __html: highlightMatchingCharacters(sanitizeSearchResult(result.api_response || result.message || result.name), searchQuery) }} />
                                                    </li>
                                                )
                                            }
                                        </ul>
                                    </>
                                    :
                                    <></>
                            }

                            {searchError ? <p className="error-text">{searchError}</p> : <></>}

                            {!excludeBestMatch(results).length && !bestMatch.id ? <p className="tif-font-sm">No results found.</p> : <></>}
                        </div>
                        :
                        <></>
                }
            </div>
            <div className="user-holder">
                <p className="tif-color-white tif-light-color-november tif-mb-0">Owner Dashboard</p>
            </div>
        </div>
    );
};

export default DashboardTopBar;